import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  InputAdornment
} from "@mui/material";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchAttachments } from "../../../redux/slices/myAccount/attachmentSlice";
import { useLanguage } from "../../../context/LanguageContext/LanguageContext";
import CustomPagination from "../Pagination/Pagination";
import Loading from "../../UI/Loading/Loading";
const AttachmentTable = () => {
  const { isLoggedIn, user, token } = useSelector(state => state.login);
  const { attachments, error ,status} = useSelector(state => state.attachments);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { translations, language, direction } = useLanguage();
  const t = translations;
  const [filter, setFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const start = page * rowsPerPage;
  const length = rowsPerPage;
  // Handle search change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);

  };

  // Handle filter change
  const handleFilterChange = (event) => {
    setFilter(event.target.value);

  };
  // Construct the search query object
  const search = searchQuery
    ? { value: searchQuery, regex: false }
    : null;
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchAttachments({ customerFk: user.id, filter, search, start, length, language, token }));
    }
  }, [dispatch, isLoggedIn, user.id, start, length, filter, searchQuery]);

  const [baseURL, setBaseURL] = useState('');
  useEffect(() => {
      // Fetch the config.json to get the baseURL
      const fetchConfig = async () => {
        try {
          const response = await fetch('/config.json');
          const config = await response.json();
          setBaseURL(config.baseURL); 
        } catch (error) {
          console.error('Error fetching config.json:', error);
        }
      };
  
      fetchConfig();
    }, []);
  if (status==="loading") return <Loading/>;
  if (!isLoggedIn) {
    navigate("/signin");
    return null;
  }

  return (
    <Box sx={{ padding: 3, backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
      <Box display="flex" justifyContent="space-between" mb={2} sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 2,
        flexDirection: { lg: "row", md: "column", xs: "column" },
        mt: { md: "0px", xs: "30px" },
        gap: "20px"
      }}>        {/* Search Input */}
        <TextField
          variant="outlined"
          placeholder={t.searchPlaceholder}
          value={searchQuery} // Search term for filtering
          onChange={handleSearchChange}
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <InputAdornment position="end">
                <CiSearch style={{ fontSize: '28px', color: '#A4A4A4' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '300px',
            height: '56px',
            backgroundColor: '#FDFDFD',
            boxShadow: '0px 11px 23px 0px #00000005',
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': { border: 'none' },
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiInputBase-input::placeholder': { fontSize: '16px' },
          }}
        />
        {/* Filter Dropdown */}
        <Select
          value={filter}
          onChange={handleFilterChange}
          displayEmpty
          size="small"
          sx={{
            width: 200,
            borderRadius: "12px",
            color: "var(--text-color)",           // Text color
            borderColor: "var(--primary-color)", // Border color
            backgroundColor: "var(--secondary-color)", // Background color
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: "var(--primary-color)", // Border color for the input field
              },
              '&:hover fieldset': {
                borderColor: "var(--primary-color)", // Border color on hover
              },
              '&.Mui-focused fieldset': {
                borderColor: "var(--primary-color)", // Border color when focused
              },
            },
          }}
        >
          <MenuItem value="" sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.attachmentTypeLabel}</MenuItem>
          <MenuItem value="1" sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.notificationTypeContract}</MenuItem>
          <MenuItem value="2" sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.unit}</MenuItem>
          <MenuItem value="3" sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.invoice}</MenuItem>
          <MenuItem value="4" sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.workOrder}</MenuItem>
          <MenuItem value="5" sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.notifications}</MenuItem>
          <MenuItem value="6" sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.quotation}</MenuItem>
          <MenuItem value="8" sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.registration}</MenuItem>
          <MenuItem value="7" sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.other}</MenuItem>
        </Select>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>{t.attachmentTypeLabel}</strong></TableCell>
              <TableCell><strong>{t.description2}</strong></TableCell>
              <TableCell><strong>{t.date}</strong></TableCell>
              <TableCell><strong>{t.attachedFiles}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attachments.length > 0 ? (
              attachments.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{row.AttachmentTypeName}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{row.Description}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{row.AttachmentDate}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    <a href={`${baseURL}/Upload${row.FilePath}`} target="_blank" rel="noopener noreferrer">
                      {row.FileName}
                    </a>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" style={{ color: "#A4A4A4", fontWeight: "bold" }}>
                  {t.noData}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        page={page}
        setPage={setPage}
        totalPages={Math.ceil(attachments.length / rowsPerPage)}
        totalData={attachments.length}
        itemsPerPage={rowsPerPage}
      />



    </Box >
  );
};

export default AttachmentTable;
